import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import Reveal from '../Reveal/Reveal';
import ArrowIcon from '../icons/ArrowIcon';
import { trackEvent } from '../../utils/analytics';

const SERVICE_OPTIONS = [
  'Custom Software Development',
  'Web Application Development',
  'Mobile App Development',
  'Cloud, DevOps & Managed IT',
  'UI / UX & Product Design',
  'API Integration & Automation',
  'Dedicated Development Team',
  'Other / Not sure yet',
];

const getEmailJsConfig = () => ({
  serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID || '',
  templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '',
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '',
});

const isEmailJsConfigured = ({ serviceId, templateId, publicKey }) =>
  Boolean(
    serviceId &&
      templateId &&
      publicKey &&
      serviceId !== 'YOUR_SERVICE_ID' &&
      templateId !== 'YOUR_TEMPLATE_ID' &&
      publicKey !== 'YOUR_PUBLIC_KEY'
  );

/**
 * Contact form — validates input and sends via EmailJS.
 */
const HomeContactForm = () => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    service: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    e.target.classList.remove('invalid');
  };

  const validate = () => {
    let valid = true;
    const fields = document.querySelectorAll(
      '#contactForm input[required], #contactForm select[required], #contactForm textarea[required]'
    );

    fields.forEach((field) => {
      const value = (field.value || '').trim();
      let fieldValid = value.length > 0;
      if (fieldValid && field.type === 'email') {
        fieldValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      }
      field.classList.toggle('invalid', !fieldValid);
      if (!fieldValid) valid = false;
    });

    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });

    if (!validate()) {
      setStatus({ type: 'error', message: 'Please complete all required fields correctly.' });
      return;
    }

    const config = getEmailJsConfig();
    if (!isEmailJsConfigured(config)) {
      setStatus({
        type: 'error',
        message:
          'Email is not configured yet. Add EmailJS keys to the .env file and restart the server.',
      });
      console.error('EmailJS missing. Set REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID, REACT_APP_EMAILJS_PUBLIC_KEY in .env');
      return;
    }

    setSending(true);

    const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();
    const templateParams = {
      to_email: 'info@codeinventivsolutions.com',
      from_name: fullName,
      from_email: form.email.trim(),
      phone: 'Not provided',
      service: form.service || 'Not specified',
      budget: 'Not specified',
      hear_about: 'Website contact form',
      message: form.message.trim(),
      reply_to: form.email.trim(),
    };

    try {
      emailjs.init({ publicKey: config.publicKey });
      await emailjs.send(config.serviceId, config.templateId, templateParams);

      trackEvent('contact_form_submit', {
        event_category: 'engagement',
        event_label: 'Home Contact Form',
        service_interest: form.service || 'Not specified',
      });

      setForm({ firstName: '', lastName: '', email: '', service: '', message: '' });
      setStatus({
        type: 'success',
        message: "Thank you — we'll be in touch within 24 hours.",
      });
    } catch (error) {
      const detail =
        (typeof error?.text === 'string' && error.text) ||
        (typeof error?.message === 'string' && error.message) ||
        '';
      console.error('EmailJS send failed:', error);
      setStatus({
        type: 'error',
        message: detail
          ? `Email failed: ${detail}`
          : 'Sorry, we could not send your message. Please try again or email us directly at info@codeinventivsolutions.com',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Reveal>
      <form className="form-box" id="contactForm" noValidate onSubmit={handleSubmit}>
        <h3 className="form-title">Request a Free Consultation</h3>
        <p className="form-sub">Tell us what you need — we reply within one business day.</p>

        <div className="form-2col">
          <div className="form-row">
            <label htmlFor="firstName">First Name</label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              placeholder="First Name"
              required
              value={form.firstName}
              onChange={handleChange}
            />
          </div>
          <div className="form-row">
            <label htmlFor="lastName">Last Name</label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              placeholder="Last Name"
              required
              value={form.lastName}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <label htmlFor="email">Email Address</label>
          <input
            id="email"
            name="email"
            type="email"
            placeholder="you@company.com"
            required
            value={form.email}
            onChange={handleChange}
          />
        </div>

        <div className="form-row">
          <label htmlFor="service">Service Required</label>
          <select id="service" name="service" required value={form.service} onChange={handleChange}>
            <option value="">Select a service…</option>
            {SERVICE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <label htmlFor="message">Your Message</label>
          <textarea
            id="message"
            name="message"
            rows={4}
            placeholder="Tell us about your project…"
            required
            value={form.message}
            onChange={handleChange}
          />
        </div>

        <button className="btn btn-gold form-submit" type="submit" disabled={sending}>
          <span className="form-submit-text">{sending ? 'Sending…' : 'Send Message'}</span>
          <ArrowIcon />
        </button>

        <p
          className={`form-status ${status.type}`}
          id="formStatus"
          role="status"
          aria-live="polite"
        >
          {status.message}
        </p>
      </form>
    </Reveal>
  );
};

export default HomeContactForm;
